import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import Clickable from 'components/Common/Clickable'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'components/Luxkit/Button/TextButton'
import Divider from 'components/Luxkit/Divider'
import LineChairWithAirplaneIcon from 'components/Luxkit/Icons/line/LineChairWithAirplaneIcon'
import LineRoomUpgradeIcon from 'components/Luxkit/Icons/line/LineRoomUpgradeIcon'
import LineTaxiIcon from 'components/Luxkit/Icons/line/LineTaxiIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Colour from 'components/utils/Colour'
import Group from 'components/utils/Group'
import withMountGuard from 'hocs/withMountGuard'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { rem } from 'polished'
import React, { useCallback } from 'react'
import styled from 'styled-components'

const Container = styled(Clickable)`
  padding: ${rem(12)} ${rem(16)};
  color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
  border-radius: ${props => props.theme.borderRadius.M};
  /** To see the effect of the border-radius */
  overflow: hidden;
`

const CustomDivider = styled(Divider)`
  && {
    background-color: ${props => props.theme.palette.product.loyalty.gold.darkContrast};
  }
`

interface Props {
  onLinkClick: () => void
}

function AccountMenuLoyaltyNoAccountGoldTile({ onLinkClick }: Props) {
  const dispatch = useAppDispatch()

  const handleActivateMembership = useCallback(() => {
    dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
    onLinkClick()
  }, [dispatch, onLinkClick])

  return <Container to={`/${LOYALTY_LANDING_PAGE}`} onClick={onLinkClick}>
    <VerticalSpacer gap={20}>
      <VerticalSpacer gap={12}>
        <Group direction="horizontal" horizontalAlign="space-between">
          <LoyaltyProgramNameLogo width={72} />
          <BodyText variant="medium" format="uppercase">Gold</BodyText>
        </Group>
        <LoyaltyTierLabel
          tier="gold"
          kind="primary"
          format="uppercase"
        >
          EXCLUSIVE INVITE
        </LoyaltyTierLabel>
        <Heading variant="heading4">
          You've unlocked{' '}
          <Colour value="tier-gold-light-contrast">Gold status</Colour>
        </Heading>
      </VerticalSpacer>
      <VerticalSpacer gap={8}>
        <BodyText variant="small" weight="bold">
          Your benefits, ready when you are
        </BodyText>
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
          <BodyTextBlock variant="small" startIcon={<LineTaxiIcon />}>
            Free airport chauffeur service
          </BodyTextBlock>
          <LoyaltyTierLabel kind="tertiary" tier="gold">
            2x
          </LoyaltyTierLabel>
        </Group>
        <CustomDivider kind="primary" />
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
          <BodyTextBlock variant="small" startIcon={<LineRoomUpgradeIcon />}>
            Guaranteed free room upgrade
          </BodyTextBlock>
          <LoyaltyTierLabel kind="tertiary" tier="gold">
            1x
          </LoyaltyTierLabel>
        </Group>
        <CustomDivider kind="primary" />
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
          <BodyTextBlock variant="small" startIcon={<LineChairWithAirplaneIcon />}>
            Free airport lounge passes
          </BodyTextBlock>
          <LoyaltyTierLabel kind="tertiary" tier="gold">
            1x
          </LoyaltyTierLabel>
        </Group>
      </VerticalSpacer>
      <TextButton kind="primary" size="medium" fit="full-width" onClick={handleActivateMembership}>
        Activate free membership
      </TextButton>
    </VerticalSpacer>
  </Container>
}

export default withMountGuard(() => {
  return useAppSelector(getIsLoyaltyEnabled)
})(AccountMenuLoyaltyNoAccountGoldTile)
